import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { SCUIButton } from '../../../../atomic/atoms';
import { Analytics, Circle, Close, Refresh, Today } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getLogs, setLogFilter } from '../../../../stores/redux/actions/campaignManagementActions';
import { GridSortModel } from '@mui/x-data-grid';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import {
    CampaignSortModel,
    CampaignStatuses,
    LogTimeRange,
    LogTimeRanges,
} from '../../../../types/enums/campaign-enums';
import { format, subHours, subMinutes } from 'date-fns';
import CopyToClipboardButton from '../SCUICopyClipboardButton/SCUICopyClipboardButton';

type DataListProps = {
    columns: ExtendedGridColDef[];
    page: number;
    setPage: any;
    pageSize: number;
    setPageSize: any;
    sortModel: GridSortModel;
    setSortModel: any;
};

type GetLogListFilterType = {
    timeRange: number;
    dateRange: {
        beginDate: string;
        endDate: string;
    };
    skipCount: number;
    maxResultCount: number;
    sortModel: GridSortModel;
};

export const SCUILogs = ({ ...props }) => {
    const {
        campaignService: { id, campaignDetail },
    } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const regionStore = useSelector((state: any) => state.region);
    const logFilterStore = useSelector((state: any) => state.campaignLogFilter);

    const [isOpen, setIsOpen] = useState<boolean>();

    const columns: ExtendedGridColDef[] = [
        {
            field: 'createDate',
            headerName: t('campaignManagement.logs.list.column.createdDate'),
            flex: 0.7,
            renderCell: (cellInfo) => (
                <Chip
                    label={new Intl.DateTimeFormat(navigator.language, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                    }).format(new Date(cellInfo.row.createDate))}
                    icon={<Today />}
                />
            ),
            sortable: false,
        },
        {
            field: 'currentCampaignTitle',
            headerName: t('campaignManagement.logs.list.column.title'),
            flex: 0.6,
            hide: true,
            renderCell: (cellInfo) => (
                <>
                    <Tooltip
                        title={t(
                            CampaignStatuses.find((x: any) => x.id == cellInfo.row.currentCampaignStatus)?.name ?? ''
                        )}
                        style={{ fontSize: '16px!important' }}
                    >
                        <Circle
                            style={{
                                height: 12,
                                width: 12,
                                fontSize: '12px',
                                color: CampaignStatuses.find((x: any) => x.id == cellInfo.row.currentCampaignStatus)
                                    ?.color,
                                marginRight: 8,
                            }}
                        />
                    </Tooltip>
                    {cellInfo.row.currentCampaignTitle}
                </>
            ),
            sortable: false,
        },
        {
            field: 'email',
            headerName: t('campaignManagement.logs.list.column.email'),
            flex: 0.5,
            hide: true,
            sortable: false,
        },
        {
            field: 'transactionId',
            headerName: t('campaignManagement.logs.list.column.transactionId'),
            flex: 0.5,
            hide: true,
            sortable: false,
        },
        {
            field: 'methodName',
            headerName: t('campaignManagement.logs.list.column.methodName'),
            flex: 0.6,
            sortable: false,
        },
        {
            field: 'message',
            headerName: t('campaignManagement.logs.list.column.message'),
            flex: 2,
            renderCell: (cellInfo) => (
                <span style={{ color: cellInfo.row.status == 0 ? 'red' : 'green' }}>{cellInfo.row.message}</span>
            ),
            sortable: false,
        },
        {
            field: 'campaignVersion',
            headerName: t('campaignManagement.logs.list.column.version'),
            flex: 0.5,
            hide: true,
            sortable: false,
        },
        {
            field: 'request',
            headerName: t('campaignManagement.logs.list.column.request'),
            flex: 0.3,
            renderCell: (cellInfo) => (
                <Tooltip title={t('campaignManagement.logs.list.copyButton')} placement="top">
                    <CopyToClipboardButton text={cellInfo.row.request} />
                </Tooltip>
            ),
            sortable: false,
        },
        {
            field: 'response',
            headerName: t('campaignManagement.logs.list.column.response'),
            flex: 0.3,
            renderCell: (cellInfo) => (
                <Tooltip title={t('campaignManagement.logs.list.copyButton')} placement="top">
                    <CopyToClipboardButton text={cellInfo.row.response} />
                </Tooltip>
            ),
            sortable: false,
        },
    ];

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'createDate',
            sort: 'desc',
        },
    ]);

    const formatedDate = (timeRange: number, dateRange: any) => {
        const date = new Date();

        let beginDate: string = '';
        let endDate: string = '';

        switch (timeRange) {
            case LogTimeRange.Last5Minute:
                beginDate = format(subMinutes(date, 5), "yyyy-MM-dd'T'HH:mm"); //5 dk eksilt ve tarihi formatla
                break;
            case LogTimeRange.Last10Minute:
                beginDate = format(subMinutes(date, 10), "yyyy-MM-dd'T'HH:mm"); //10 dk eksilt ve tarihi formatla
                break;
            case LogTimeRange.Last30Minute:
                beginDate = format(subMinutes(date, 30), "yyyy-MM-dd'T'HH:mm"); //10 dk eksilt ve tarihi formatla
                break;
            case LogTimeRange.CustomRange:
                beginDate = dateRange.beginDate;
                endDate = dateRange.endDate;
                break;
        }

        return {
            dateRange: {
                beginDate,
                endDate,
            },
        };
    };

    const getLogList = (filter: GetLogListFilterType) => {
        const { skipCount, maxResultCount, sortModel } = filter;

        const {
            dateRange: { beginDate, endDate },
        } = formatedDate(logFilterStore.timeRange, logFilterStore.dateRange);

        const campaignId = id;
        const regionId = Number(regionStore.region);
        const email = campaignDetail?.campaignRule?.assignee ?? '';

        let sortPropertyName = sortModel[0]?.field ?? 'id';
        let sortValue = sortModel[0].sort == 'asc' ? CampaignSortModel.Ascending : CampaignSortModel.Descending;

        dispatch(
            getLogs({
                regionId,
                campaignId,
                email,
                beginDate,
                endDate,
                skipCount,
                maxResultCount,
                sortPropertyName,
                sortValue,
            })
        );
    };

    useEffect(() => {
        getLogList({
            timeRange: logFilterStore.timeRange,
            dateRange: logFilterStore.dateRange,
            skipCount: page,
            maxResultCount: pageSize,
            sortModel,
        });
    }, [isOpen, page, pageSize, sortModel]);

    const handleButtonClick = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleRefresh = () => {
        getLogList({
            timeRange: logFilterStore.timeRange,
            dateRange: logFilterStore.dateRange,
            skipCount: page,
            maxResultCount: pageSize,
            sortModel,
        });
    };

    return (
        <>
            <SCUIButton
                text={'Logs'}
                type={'secondary'}
                sizeType="icon"
                size={'s'}
                buttonType="button"
                showStartIcon={true}
                startIcon={<Analytics style={{ height: 24, width: 24, color: '#2962ff' }} />}
                onClick={handleButtonClick}
            />

            {isOpen && (
                <BootstrapDialog
                    onClose={handleClose}
                    aria-labelledby="log-activities"
                    open={isOpen}
                    maxWidth={'xl'}
                    fullWidth
                >
                    <DialogTitle sx={{ m: 1, p: 1 }} id="customized-dialog-title" style={{ border: 0 }}>
                        <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                            {t('campaignManagement.logs.modal.title')}
                        </Typography>
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>

                    <div style={{ margin: '0 1.5rem 1.5rem 1.5rem' }}>
                        <FilterToolbar actionRefresh={handleRefresh} />
                    </div>

                    <DialogContent dividers>
                        <DataList
                            columns={columns}
                            page={page}
                            setPage={setPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            sortModel={sortModel}
                            setSortModel={setSortModel}
                        />
                    </DialogContent>
                </BootstrapDialog>
            )}
        </>
    );
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(0),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(0),
    },
    '& .MuiGrid-item': {
        paddingTop: `${theme.spacing(0)}!important`,
    },
}));

const DataList = ({ ...props }: DataListProps) => {
    const { columns, page, setPage, pageSize, setPageSize, sortModel, setSortModel } = props;

    const logStore = useSelector((state: any) => state.campaignLogs);
    const { data, loading } = logStore;

    const handleSortChange = useCallback((model: GridSortModel) => {
        if (JSON.stringify(model) !== JSON.stringify(sortModel)) {
            setSortModel(model);
        }
    }, []);

    const handleColumnVisible = (model: any) => {
        let _columns = [...columns];
        _columns.map((x: any) => {
            if (Object.entries(model).some((y: any) => y[0] == x.field)) {
                x.hide = !model[x.field];
            }
        });
    };

    return (
        <SCUICustomDataGrid
            style={{ marginTop: '0!important' }}
            randomId
            loading={loading}
            columns={columns}
            rows={data?.items ?? []}
            rowCount={data?.totalCount ?? 0}
            pagination
            page={page}
            pageSize={pageSize}
            paginationModeChange={true}
            onPageChange={(newPage: number) => setPage(newPage)}
            onPageSizeChange={(newPageSize: number) => {
                setPageSize(newPageSize);
                setPage(0);
            }}
            sortingMode="server"
            onSortModelChange={(model: GridSortModel) => handleSortChange(model)}
            onColumnVisibilityModelChange={(newModel: any) => handleColumnVisible(newModel)}
        />
    );
};

const toolbarSx = {
    display: 'flex',
    width: '100%',
};

const actionsSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
};

const FilterToolbar = ({ actionRefresh }: { actionRefresh?: () => void }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const logFilterStore = useSelector((state: any) => state.campaignLogFilter);

    const timeRangeHandleChange = (event: any) => {
        const { value } = event.target;

        let _logFilterStore = { ...logFilterStore, timeRange: value };

        const date = new Date();
        const adjustedDate = subHours(date, 1); // 1 saat eksilt
        const beginFormattedDate = format(adjustedDate, "yyyy-MM-dd'T'HH:mm"); //tarihi formatla
        const endFormattedDate = format(date, "yyyy-MM-dd'T'HH:mm"); //tarihi formatla

        switch (value) {
            case LogTimeRange.Last5Minute:
            case LogTimeRange.Last10Minute:
            case LogTimeRange.Last30Minute:
                _logFilterStore = { ..._logFilterStore, dateRange: { beginDate: '', endDate: '' } };
                break;
            case LogTimeRange.CustomRange:
                _logFilterStore = {
                    ..._logFilterStore,
                    dateRange: { beginDate: beginFormattedDate, endDate: endFormattedDate },
                };
                break;
        }

        dispatch(setLogFilter(_logFilterStore));
    };

    const handleRefresh = () => {
        actionRefresh?.();
    };
    return (
        <Box sx={toolbarSx}>
            <FormControl sx={{ mr: 2, minWidth: 180 }}>
                <TextField
                    select
                    id="timeRange"
                    name="timeRange"
                    label={t('campaignManagement.logs.list.filter.timeRange.title')}
                    variant="outlined"
                    slotProps={{
                        select: {
                            label: '',
                            multiple: false,
                            value: logFilterStore.timeRange,
                        },
                    }}
                    onChange={timeRangeHandleChange}
                >
                    {LogTimeRanges.length > 0 &&
                        LogTimeRanges.map((item: any) => {
                            return (
                                <MenuItem value={item.id} key={item.id}>
                                    {t(item.name)}
                                </MenuItem>
                            );
                        })}
                </TextField>
            </FormControl>

            {logFilterStore.timeRange == LogTimeRange.CustomRange && (
                <>
                    <FormControl sx={{ mr: 2, minWidth: 220 }}>
                        <TextField
                            id="beginDate"
                            label={t('campaignManagement.save.step.properties.beginDate.title')}
                            type="datetime-local"
                            value={logFilterStore.dateRange.beginDate}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            variant="outlined"
                            disabled={logFilterStore.dateRange.beginDate == ''}
                            onChange={(event) => {
                                const { value } = event.target;
                                dispatch(
                                    setLogFilter({
                                        ...logFilterStore,
                                        dateRange: { ...logFilterStore.dateRange, beginDate: value },
                                    })
                                );
                            }}
                        />
                    </FormControl>

                    <FormControl sx={{ mr: 2, minWidth: 220 }}>
                        <TextField
                            id="endDate"
                            label={t('campaignManagement.save.step.properties.endDate.title')}
                            type="datetime-local"
                            value={logFilterStore.dateRange.endDate}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            variant="outlined"
                            disabled={logFilterStore.dateRange.endDate == ''}
                            onChange={(event) => {
                                const { value } = event.target;
                                dispatch(
                                    setLogFilter({
                                        ...logFilterStore,
                                        dateRange: { ...logFilterStore.dateRange, endDate: value },
                                    })
                                );
                            }}
                        />
                    </FormControl>
                </>
            )}

            <Box sx={actionsSx}>
                <SCUIButton
                    text={t('campaignManagement.logs.list.filter.refreshButton')}
                    type="secondary"
                    sizeType="icon"
                    size={'s'}
                    showStartIcon={true}
                    startIcon={<Refresh style={{ height: 24, width: 24, color: '#0d6efd' }} />}
                    onClick={handleRefresh}
                />
            </Box>
        </Box>
    );
};
